import { useState, useEffect, useRef, useCallback } from "react";
import { apiCall } from "../api";

const POLL_INTERVAL_MS = 60 * 1000;

const BELL_ICON = 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9';

const ICONS = {
  'bell': BELL_ICON,
  'user-plus': 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  'dollar-sign': 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  'alert-triangle': 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z',
  'message-circle': 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  'home': 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  'shield-check': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  'star': 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  'document': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
};

// Background and icon color for each notification type
const TYPE_COLORS = {
  dispute: 'bg-red-100 text-red-600',
  payment: 'bg-green-100 text-green-600',
  report: 'bg-green-100 text-green-600',
  welcome: 'bg-amber-100 text-amber-600',
  response: 'bg-yellow-100 text-yellow-600',
  property: 'bg-purple-100 text-purple-600',
  rating: 'bg-amber-100 text-amber-600',
  promo: 'bg-amber-100 text-amber-600',
  verification: 'bg-blue-100 text-blue-600',
};

// Dashboard tab each notification type opens; dispute and response open a detail modal instead
const TYPE_TABS = {
  payment: 'analytics',
  rating: 'analytics',
  report: 'search',
  verification: 'rate',
  promo: 'rate',
  property: 'analytics',
};

// Older notifications were stored without a UTC offset; read them as UTC, not local time
const parseServerDate = (timestamp) => {
  if (!timestamp) return new Date(NaN);
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(timestamp);
  return new Date(hasOffset ? timestamp : `${timestamp}Z`);
};

const formatTimeAgo = (timestamp) => {
  const diffInMinutes = Math.max(0, Math.floor((Date.now() - parseServerDate(timestamp)) / (1000 * 60)));
  if (diffInMinutes < 1) return 'ahora';
  if (diffInMinutes < 60) return `hace ${diffInMinutes} min`;
  if (diffInMinutes < 1440) return `hace ${Math.floor(diffInMinutes / 60)} h`;
  const days = Math.floor(diffInMinutes / 1440);
  return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
};

const formatFullDate = (timestamp) => {
  const date = parseServerDate(timestamp);
  return isNaN(date) ? '' : date.toLocaleString('es', { dateStyle: 'long', timeStyle: 'short' });
};

const groupByDate = (notifications) => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6);
  const groups = [
    { label: 'Hoy', items: [] },
    { label: 'Esta semana', items: [] },
    { label: 'Anteriores', items: [] },
  ];
  notifications.forEach((n) => {
    const date = parseServerDate(n.created_at);
    const group = date >= startOfToday ? 0 : date >= startOfWeek ? 1 : 2;
    groups[group].items.push(n);
  });
  return groups.filter((g) => g.items.length > 0);
};

const DisputeDetail = ({ disputeId, token }) => {
  const [dispute, setDispute] = useState(null);
  const [error, setError] = useState('');
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiCall(`/disputes/${disputeId}`, 'GET', null, token)
      .then(setDispute)
      .catch((err) => setError(err.detail || 'No se pudo cargar la disputa'));
  }, [disputeId, token]);

  const resolve = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall(`/disputes/${disputeId}/resolve`, 'PUT', { resolution }, token);
      setDispute((prev) => ({ ...prev, status: 'resolved', resolution }));
    } catch (err) {
      setError(err.detail || 'No se pudo guardar tu respuesta');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!dispute) return <p className="text-sm text-gray-500">Cargando...</p>;

  return (
    <div className="space-y-4 text-sm">
      <div>
        <p className="text-gray-500">Inquilino</p>
        <p className="font-semibold text-gray-900">{dispute.tenant_name} <span className="font-normal text-gray-500">({dispute.tenant_email})</span></p>
      </div>
      {dispute.rating && (
        <div className="bg-amber-50 rounded-lg p-3">
          <p className="text-gray-500">Tu calificaci&oacute;n</p>
          <p className="font-semibold text-gray-900">{dispute.rating.overall_rating}/5{dispute.rating.property_address ? ` · ${dispute.rating.property_address}` : ''}</p>
          {dispute.rating.comments && <p className="text-gray-700 mt-1">{dispute.rating.comments}</p>}
        </div>
      )}
      <div>
        <p className="text-gray-500">Motivo</p>
        <p className="font-semibold text-gray-900">{dispute.dispute_reason}</p>
      </div>
      <div>
        <p className="text-gray-500">Detalles</p>
        <p className="text-gray-800 whitespace-pre-line">{dispute.dispute_details}</p>
      </div>
      {dispute.evidence_description && (
        <div>
          <p className="text-gray-500">Pruebas que menciona</p>
          <p className="text-gray-800 whitespace-pre-line">{dispute.evidence_description}</p>
        </div>
      )}
      {dispute.status === 'resolved' ? (
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-green-700 font-semibold">Disputa resuelta</p>
          {dispute.resolution && <p className="text-gray-800 mt-1">{dispute.resolution}</p>}
        </div>
      ) : (
        <form onSubmit={resolve} className="space-y-2">
          <label className="block text-gray-500">Tu respuesta</label>
          <textarea
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            required
            rows={3}
            className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            placeholder={'Explica tu postura o qu\u00e9 corregiste'}
          />
          <button
            type="submit"
            disabled={saving}
            className="w-full py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Resolver disputa'}
          </button>
        </form>
      )}
    </div>
  );
};

const TenantResponses = ({ ratingId, token }) => {
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiCall(`/tenants/responses/${ratingId}`, 'GET', null, token)
      .then(setResponses)
      .catch((err) => setError(err.detail || 'No se pudieron cargar las respuestas'));
  }, [ratingId, token]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!responses) return <p className="text-sm text-gray-500">Cargando...</p>;
  if (responses.length === 0) return <p className="text-sm text-gray-500">No hay respuestas.</p>;

  return (
    <div className="space-y-3 text-sm">
      {responses.map((r) => (
        <div key={r.id} className="border border-gray-200 rounded-lg p-3">
          <div className="flex justify-between gap-2">
            <p className="font-semibold text-gray-900">{r.tenant_name}</p>
            <p className="text-xs text-gray-400" title={formatFullDate(r.created_at)}>{formatTimeAgo(r.created_at)}</p>
          </div>
          <p className="text-gray-800 mt-1 whitespace-pre-line">{r.response_text}</p>
          {r.evidence_description && <p className="text-gray-500 mt-2">Pruebas: {r.evidence_description}</p>}
        </div>
      ))}
    </div>
  );
};

const NotificationDetailModal = ({ notification, token, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[600]" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg text-left max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-amber-100 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800">
          {notification.type === 'dispute' ? 'Disputa del inquilino' : 'Respuestas del inquilino'}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none" aria-label="Cerrar">&times;</button>
      </div>
      <div className="p-4">
        {notification.type === 'dispute'
          ? <DisputeDetail disputeId={notification.related_id} token={token} />
          : <TenantResponses ratingId={notification.related_id} token={token} />}
      </div>
    </div>
  </div>
);

// Notifications bell in the dashboard header
const NotificationsBell = ({ token, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(80);
  const [detail, setDetail] = useState(null);
  const buttonRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const result = await apiCall('/notifications', 'GET', null, token);
      setNotifications(result.notifications || []);
      setUnreadCount(result.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, [token]);

  // Load on mount, then refresh every minute while the tab is visible and whenever it regains focus
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadNotifications();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadNotifications();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadNotifications]);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !event.target.closest('.notifications-container')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggle = () => {
    if (!open && buttonRef.current) {
      // The header wraps onto two lines on small screens, so place the dropdown under the button
      setDropdownTop(buttonRef.current.getBoundingClientRect().bottom + 8);
    }
    setOpen(!open);
    if (!open) loadNotifications();
  };

  const markAsRead = async (notificationId) => {
    setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await apiCall(`/notifications/${notificationId}/read`, 'PUT', null, token);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      loadNotifications();
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('¿Eliminar esta notificación?')) return;
    try {
      await apiCall(`/notifications/${notificationId}`, 'DELETE', null, token);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiCall('/notifications/mark-all-read', 'PUT', null, token);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const openNotification = (notification) => {
    if (!notification.is_read) markAsRead(notification.id);
    if ((notification.type === 'dispute' || notification.type === 'response') && notification.related_id) {
      setOpen(false);
      setDetail(notification);
    } else if (TYPE_TABS[notification.type]) {
      setOpen(false);
      onNavigate(TYPE_TABS[notification.type]);
    }
  };

  const isClickable = (n) => Boolean(
    ((n.type === 'dispute' || n.type === 'response') && n.related_id) || TYPE_TABS[n.type]
  );

  return (
    <div className="relative notifications-container">
      <button
        ref={buttonRef}
        onClick={toggle}
        aria-label="Notificaciones"
        className="relative p-2.5 sm:p-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={BELL_ICON} />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed right-4 w-80 sm:w-96 text-left bg-white rounded-xl shadow-2xl border border-gray-200 z-[500] overflow-hidden"
          style={{ top: dropdownTop, maxWidth: 'calc(100vw - 2rem)', maxHeight: `calc(100vh - ${dropdownTop + 16}px)` }}
        >
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-amber-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">Notificaciones</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-amber-600 hover:text-amber-800 font-semibold"
                >
                  Marcar todas como le&iacute;das
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: `calc(100vh - ${dropdownTop + 90}px)` }}>
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={BELL_ICON} />
                </svg>
                <p className="text-sm">No tienes notificaciones</p>
              </div>
            ) : (
              groupByDate(notifications).map((group) => (
                <div key={group.label}>
                  <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{group.label}</p>
                  {group.items.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => openNotification(notification)}
                      className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        isClickable(notification) ? 'cursor-pointer' : ''
                      } ${!notification.is_read ? 'bg-amber-50 border-l-4 border-l-amber-500' : ''}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${TYPE_COLORS[notification.type] || 'bg-gray-100 text-gray-600'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={ICONS[notification.icon] || BELL_ICON} />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-400" title={formatFullDate(notification.created_at)}>
                              {formatTimeAgo(notification.created_at)}
                            </p>
                            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                              {!notification.is_read && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="text-xs text-amber-600 hover:text-amber-800 font-semibold"
                                >
                                  Marcar como le&iacute;da
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="text-xs text-red-600 hover:text-red-800 font-semibold"
                              >
                                Eliminar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {detail && (
        <NotificationDetailModal notification={detail} token={token} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export { NotificationsBell, parseServerDate, formatTimeAgo };
